import { EventEmitter } from '@apia/util';

export type StreamBlock = {
  arguments?: string;
  description: string;
  id?: string;
  response?: string;
  type: string;
};

export type StreamBlockResponse = {
  id: string;
  response: string;
};

export type AssistantStreamEvent =
  | {
      id?: string;
      kind: 'assistant_intermediate_start';
      seq: number;
    }
  | {
      id?: string;
      kind: 'assistant_intermediate';
      seq: number;
      text: string;
    }
  | {
      kind: 'assistant_final';
      seq: number;
      text: string;
    }
  | {
      arguments?: string;
      blockType: string;
      content: string;
      id?: string;
      kind: 'block';
      seq: number;
    }
  | {
      id: string;
      kind: 'block_response';
      response: string;
      seq: number;
    };

export type ResponseStreamEvents = {
  block: StreamBlock;
  blockResponse: StreamBlockResponse;
  finalPart: string;
  intermediateTextStart: { id?: string } | null;
  part: string;
};

export type ResponseStreamBlock = { type: string; content: string; id: string };

type BufferedTextEvent = 'finalPart' | 'part';

type BufferedTextChunk = {
  ev: BufferedTextEvent;
  kind: 'text';
  text: string;
};

type BufferedSignalChunk = {
  ev: 'intermediateTextStart';
  id?: string;
  kind: 'signal';
};

type BufferedChunk = BufferedSignalChunk | BufferedTextChunk;

export class ResponseStream<
  T extends ResponseStreamEvents = ResponseStreamEvents,
> extends EventEmitter<T> {
  private buffer: BufferedChunk[] = [];
  private idleResolvers: (() => void)[] = [];
  private intermediateTextId?: string;
  private isRunning = false;
  private locked = false;
  public maxCompletionTime = 150; // ms
  private minPartSize = 2;
  private partSize = 2;
  private timing = 5; // ms
  private waiting: BufferedChunk[] = [];

  private emitDirect<K extends keyof ResponseStreamEvents>(
    ev: K,
    data: ResponseStreamEvents[K],
  ) {
    super.emit(ev as keyof T, data as unknown as T[keyof T]);
  }

  private appendTextChunk(target: BufferedChunk[], chunk: BufferedTextChunk) {
    const lastChunk = target[target.length - 1];
    if (lastChunk?.kind === 'text' && lastChunk.ev === chunk.ev) {
      lastChunk.text += chunk.text;
    } else {
      target.push(chunk);
    }
  }

  private appendSignalChunk(target: BufferedChunk[], chunk: BufferedSignalChunk) {
    target.push(chunk);
  }

  private drainWaiting() {
    while (this.waiting.length > 0) {
      const chunk = this.waiting.shift();
      if (chunk) {
        if (chunk.kind === 'signal') {
          this.appendSignalChunk(this.buffer, chunk);
        } else {
          this.appendTextChunk(this.buffer, chunk);
        }
      }
    }
  }

  private enqueuePart(
    chunk: string,
    parseLegacyBlocks: boolean,
    ev: BufferedTextEvent = 'part',
  ) {
    if (parseLegacyBlocks) {
      const blockMatches = chunk.matchAll(
        /<BLOCK-START type="([^"]+)"(?: id="([^"]*)" arguments="([^"]*)")?>([\s\S]*?)<\/BLOCK-END type="\1">/g,
      );

      for (const match of blockMatches) {
        this.emit('block', {
          arguments: match[3],
          description: match[4],
          id: match[2],
          type: match[1],
        });
        chunk = chunk.replaceAll(match[0], '');
      }
    }

    if (!chunk) {
      return;
    }

    const bufferedChunk: BufferedTextChunk = { ev, kind: 'text', text: chunk };
    if (this.locked) {
      this.appendTextChunk(this.waiting, bufferedChunk);
    } else {
      this.appendTextChunk(this.buffer, bufferedChunk);
    }
    this.calculatePartSize();
    this.run();
  }

  private enqueueIntermediateTextStart(id?: string) {
    const bufferedChunk: BufferedSignalChunk = {
      ev: 'intermediateTextStart',
      id,
      kind: 'signal',
    };
    if (this.locked) {
      this.appendSignalChunk(this.waiting, bufferedChunk);
    } else {
      this.appendSignalChunk(this.buffer, bufferedChunk);
    }
    this.run();
  }

  private calculatePartSize() {
    const totalSize =
      this.buffer.reduce<number>(
        (a, b) => a + (b.kind === 'text' ? b.text.length : 0),
        0,
      ) +
      this.waiting.reduce<number>(
        (a, b) => a + (b.kind === 'text' ? b.text.length : 0),
        0,
      );

    this.partSize = Math.max(
      this.minPartSize,
      totalSize * (this.timing / this.maxCompletionTime),
    );
  }

  emit<K extends keyof T>(ev: K, data: T[K]): void {
    if (ev === 'part') {
      this.enqueuePart(data as string, true);
    } else {
      super.emit(ev, data);
    }
  }

  emitAssistantStreamEvent(event: AssistantStreamEvent) {
    if (event.kind === 'assistant_intermediate_start') {
      this.intermediateTextId = event.id;
      this.enqueueIntermediateTextStart(event.id);
      return;
    }

    if (event.kind === 'assistant_intermediate') {
      if (event.id && event.id !== this.intermediateTextId) {
        this.intermediateTextId = event.id;
        this.enqueueIntermediateTextStart(event.id);
      }
      this.enqueuePart(event.text, false);
      return;
    }

    if (event.kind === 'assistant_final') {
      this.enqueuePart(event.text, false, 'finalPart');
      return;
    }

    if (event.kind === 'block') {
      this.emitDirect('block', {
        arguments: event.arguments,
        description: event.content,
        id: event.id,
        type: event.blockType,
      });
      return;
    }

    this.emitDirect('blockResponse', {
      id: event.id,
      response: event.response,
    });
  }

  emitFinalText(text?: string) {
    if (!text) {
      return;
    }

    this.enqueuePart(text, false, 'finalPart');
  }

  emitAssistantStreamEvents(events?: AssistantStreamEvent[]) {
    if (!events?.length) {
      return;
    }

    [...events]
      .sort((left, right) => left.seq - right.seq)
      .forEach((event) => {
        this.emitAssistantStreamEvent(event);
      });
  }

  waitUntilIdle() {
    if (!this.isRunning && this.buffer.length === 0 && this.waiting.length === 0) {
      return Promise.resolve();
    }

    return new Promise<void>((resolve) => {
      this.idleResolvers.push(resolve);
    });
  }

  private resolveIdle() {
    if (this.idleResolvers.length === 0) {
      return;
    }

    const resolvers = this.idleResolvers;
    this.idleResolvers = [];
    resolvers.forEach((resolve) => resolve());
  }

  run() {
    if (!this.isRunning) {
      this.isRunning = true;
      const interval = setInterval(() => {
        this.locked = true;
        this.drainWaiting();

        const currentChunk = this.buffer[0];
        if (currentChunk?.kind === 'signal') {
          this.buffer.shift();
          this.locked = false;
          this.emitDirect(currentChunk.ev, { id: currentChunk.id });
          if (this.buffer.length === 0 && this.waiting.length === 0) {
            this.isRunning = false;
            clearInterval(interval);
            this.resolveIdle();
          }
          return;
        }

        const choosedSize = Math.min(
          currentChunk?.text.length ?? 0,
          this.partSize,
        );

        const part = currentChunk?.text.slice(0, choosedSize) ?? '';
        const ev = currentChunk?.ev;
        if (currentChunk) {
          currentChunk.text = currentChunk.text.slice(choosedSize);
          if (!currentChunk.text) {
            this.buffer.shift();
          }
        }

        this.locked = false;

        if (part && ev) {
          this.emitDirect(ev, part);
          this.calculatePartSize();
          if (this.buffer.length === 0 && this.waiting.length === 0) {
            this.isRunning = false;
            clearInterval(interval);
            this.resolveIdle();
          }
        } else {
          this.isRunning = false;
          clearInterval(interval);
          this.resolveIdle();
        }
      }, this.timing);
    }
  }
}
